// src/features/streams/components/simulcast/BroadcastMetadataOverrideSection.tsx

import { useState } from 'react';
import { Stack, Text, TextInput, Paper, Checkbox } from '@mantine/core';
import type { Control, FieldErrors } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';

interface BroadcastMetadataOverrideSectionProps {
  streamTitle: string;
  streamDescription: string;
  control: Control<any>;
  errors: FieldErrors<any>;
  existingTitle?: string;
  existingDescription?: string;
}

export function BroadcastMetadataOverrideSection({
  streamTitle,
  streamDescription,
  control,
  errors,
  existingTitle,
  existingDescription,
}: BroadcastMetadataOverrideSectionProps) {
  const hasExistingOverrides =
    (existingTitle != null && existingTitle.trim().length > 0) ||
    (existingDescription != null && existingDescription.trim().length > 0);

  const [overrideEnabled, setOverrideEnabled] = useState(hasExistingOverrides);

  const { field: titleField } = useController({ name: 'title', control });
  const { field: descShortField } = useController({ name: 'descShort', control });

  const handleToggle = (checked: boolean) => {
    setOverrideEnabled(checked);
    if (!checked) {
      titleField.onChange('');
      descShortField.onChange('');
    }
  };

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">
        Broadcast Title & Description
      </Text>

      <Text size="xs" c="dimmed">
        By default, this destination will use the stream's title and description.
      </Text>

      <Paper p="sm" radius="sm" bg="blue.0">
        <Stack gap={4}>
          <Text size="xs">
            <Text span fw={500}>Title:</Text> {streamTitle || '(none)'}
          </Text>
          {streamDescription && (
            <Text size="xs" lineClamp={2}>
              <Text span fw={500}>Description:</Text> {streamDescription}
            </Text>
          )}
        </Stack>
      </Paper>

      <Checkbox
        label="Override title and description"
        checked={overrideEnabled}
        onChange={(e) => handleToggle(e.currentTarget.checked)}
      />

      {overrideEnabled && (
        <Stack gap="sm">
          <TextInput
            label="Title"
            placeholder={streamTitle || 'Enter title'}
            value={titleField.value}
            onChange={titleField.onChange}
            onBlur={titleField.onBlur}
            error={(errors.title as any)?.message}
            maxLength={SIMULCAST_CONSTRAINTS.TITLE_MAX}
            description={`${titleField.value.length}/${SIMULCAST_CONSTRAINTS.TITLE_MAX}`}
          />

          <TextInput
            label="Description"
            placeholder={streamDescription || 'Enter description'}
            value={descShortField.value}
            onChange={descShortField.onChange}
            onBlur={descShortField.onBlur}
            error={(errors.descShort as any)?.message}
            maxLength={SIMULCAST_CONSTRAINTS.DESC_SHORT_MAX}
            description={`${descShortField.value.length}/${SIMULCAST_CONSTRAINTS.DESC_SHORT_MAX}`}
          />
        </Stack>
      )}
    </Stack>
  );
}
